import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "./api/client";
import SearchableSettingSelect from "./components/SearchableSettingSelect";
import { getAllCityOptions, getAllCountryOptions, getCityOptionByName, getCountryOptionByName } from "./locationOptions";
import SettingsSavedNotice from "./components/SettingsSavedNotice";
import { useCurrentUser } from "./currentUserContext";

function createDraft(settings = {}) {
  return {
    displayName: String(settings.displayName || ""),
    city: String(settings.city || ""),
    country: String(settings.country || "")
  };
}

function sanitizeInput(value, maxLength = 120) {
  return String(value ?? "").slice(0, maxLength);
}

function renderCountryOption(_value, option) {
  return (
    <>
      <span className={`fi fi-${option.flagCode} account-setting-select-preview-flag`} aria-hidden="true" />
      <span className="account-setting-select-preview-label">{option.label}</span>
    </>
  );
}

function renderCityOption(_value, option) {
  return <span className="account-setting-select-preview-label">{option.label}</span>;
}

export default function GeneralSettingsView() {
  const { selectedUserId, selectedUser } = useCurrentUser();
  const [draft, setDraft] = useState(() => createDraft());
  const [savedDraft, setSavedDraft] = useState(() => createDraft());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const selectedCountryOption = useMemo(() => getCountryOptionByName(draft.country), [draft.country]);
  const selectedCityOption = useMemo(() => (
    selectedCountryOption ? getCityOptionByName(selectedCountryOption.label, draft.city) : null
  ), [draft.city, selectedCountryOption]);
  const cityOptions = useMemo(() => (
    selectedCountryOption ? getAllCityOptions(selectedCountryOption.label) : []
  ), [selectedCountryOption]);
  const validationError = useMemo(() => {
    if (draft.country.trim() && !selectedCountryOption) {
      return "Choose a valid country from the list.";
    }

    if (draft.city.trim() && !selectedCountryOption) {
      return "Choose a valid country before choosing a city.";
    }

    if (draft.city.trim() && !selectedCityOption) {
      return "Choose a valid city from the list.";
    }

    return null;
  }, [draft.city, draft.country, selectedCityOption, selectedCountryOption]);

  useEffect(() => {
    let isDisposed = false;

    async function loadSettings() {
      try {
        setLoading(true);
        const response = await apiFetch(`/api/account/general-settings/${selectedUserId}`, { userId: selectedUserId });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not load General Settings");
        }

        if (isDisposed) {
          return;
        }

        const nextDraft = createDraft(data?.settings || {});
        setDraft(nextDraft);
        setSavedDraft(nextDraft);
        setError(null);
      } catch (loadError) {
        if (!isDisposed) {
          console.error("General settings load error:", loadError);
          setError(loadError.message || "Could not load General Settings");
        }
      } finally {
        if (!isDisposed) {
          setLoading(false);
        }
      }
    }

    loadSettings();

    return () => {
      isDisposed = true;
    };
  }, [selectedUserId]);

  const hasChanges = JSON.stringify(draft) !== JSON.stringify(savedDraft);

  useEffect(() => {
    if (!notice) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => setNotice(null), 3600);
    return () => window.clearTimeout(timeoutId);
  }, [notice]);

  useEffect(() => {
    if (loading || saving || !hasChanges || validationError) {
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        setSaving(true);
        setNotice(null);
        setError(null);

        const payload = {
          displayName: draft.displayName.trim(),
          city: draft.city.trim(),
          country: draft.country.trim()
        };

        const response = await apiFetch(`/api/account/general-settings/${selectedUserId}`, {
          userId: selectedUserId,
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || "Could not save General Settings");
        }

        const nextDraft = createDraft(data?.settings || payload);
        setDraft(nextDraft);
        setSavedDraft(nextDraft);
        setNotice("Settings saved");
      } catch (saveError) {
        console.error("General settings save error:", saveError);
        setError(saveError.message || "Could not save General Settings");
      } finally {
        setSaving(false);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [draft, hasChanges, loading, saving, selectedUserId, validationError]);

  return (
    <div className="account-shell">
      <div className="account-settings-page">
        <div className="account-breadcrumb">
          <Link to="/account">My Account</Link>
          <span>/</span>
          <span>General Settings</span>
        </div>

        <div className="account-settings-panel__header">
          <div>
            <h1 className="account-title">General Settings</h1>
            <p className="account-copy">
              Control how {selectedUser?.label || "this user"} appears in Browse Users. These lines are shown above the filters and view mode on the public trader page.
            </p>
          </div>
        </div>

        {error ? <div className="account-error">{error}</div> : null}
        {!error && validationError ? <div className="account-error">{validationError}</div> : null}

        {loading ? (
          <div className="account-loading">Loading General Settings...</div>
        ) : (
          <div className="account-setting-grid account-setting-grid--stacked-mobile" style={{ alignItems: "start" }}>
            <section className="account-setting-card account-setting-card--wide">
              <div className="account-setting-card__header account-setting-card__header--stacked">
                <div>
                  <h2 className="account-setting-card__title">Profile lines</h2>
                  <p className="account-setting-card__copy">
                    Add your name, city, and country to your viewed user profile.
                  </p>
                </div>
              </div>

              <div className="account-setting-card__body" style={{ gap: "14px" }}>
                <label className="account-auth-field">
                  <span>Name</span>
                  <input
                    className="account-auth-input"
                    type="text"
                    value={draft.displayName}
                    onChange={(event) => setDraft((currentDraft) => ({ ...currentDraft, displayName: sanitizeInput(event.target.value) }))}
                    placeholder="Write the name to show above the trader page"
                  />
                </label>

                <label className="account-auth-field">
                  <span>Country</span>
                  <div style={{ display: "grid", gap: "8px" }}>
                    <SearchableSettingSelect
                      title="Country"
                      value={selectedCountryOption?.value || ""}
                      options={getAllCountryOptions()}
                      onChange={(nextCountry) => setDraft((currentDraft) => ({
                        ...currentDraft,
                        country: nextCountry,
                        city: getCityOptionByName(nextCountry, currentDraft.city)?.value || ""
                      }))}
                      placeholder="Search country"
                      emptyMessage="No countries found."
                      renderSelectedValue={renderCountryOption}
                      renderOption={renderCountryOption}
                    />
                  </div>
                </label>

                <label className="account-auth-field">
                  <span>City</span>
                  <SearchableSettingSelect
                    title="City"
                    value={selectedCityOption?.value || ""}
                    options={cityOptions}
                    onChange={(nextCity) => setDraft((currentDraft) => ({ ...currentDraft, city: nextCity }))}
                    placeholder={selectedCountryOption ? "Search city" : "Choose country first"}
                    emptyMessage={selectedCountryOption ? "No cities found." : "Choose a country first."}
                    minQueryLength={selectedCountryOption ? 3 : 0}
                    maxVisibleOptions={50}
                    disabled={!selectedCountryOption}
                    renderSelectedValue={renderCityOption}
                    renderOption={renderCityOption}
                  />
                  <span style={{ fontSize: "12px", color: "#64748b" }}>
                    {selectedCountryOption
                      ? "Type at least 3 letters to search up to 50 city matches."
                      : "Select a valid country to unlock city choices."}
                  </span>
                </label>
              </div>
            </section>
          </div>
        )}
      </div>

      {notice ? <SettingsSavedNotice message={notice} /> : null}
    </div>
  );
}